import {getAgentsPath, getAgentsSpacesPath} from '@github-ui/agent-sessions/utils/constants'
import {announce} from '@github-ui/aria-live'
import {getCopilotSpacePath, spaceChatPath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotSpaceId} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {SpaceMenu} from '@github-ui/copilot-spaces/components/SpaceMenu'
import {SpacesList} from '@github-ui/copilot-spaces/components/SpacesList'
import {
  useFetchFilteredCategorizedSpaces,
  useFetchPaginatedCategorizedSpaces,
  useFetchSpacesCount,
} from '@github-ui/copilot-spaces/hooks'
import {useDeleteCopilotSpace} from '@github-ui/copilot-spaces/hooks/use-delete-copilot-space-no-chat-state'
import {ContentView} from '@github-ui/pacer/ContentView'
import {SearchInput} from '@github-ui/pacer/SearchInput'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useNavigate} from '@github-ui/use-navigate'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {CommentDiscussionIcon, PlusIcon} from '@primer/octicons-react'
import {Banner, Button, Link} from '@primer/react'
import {useRef, useState} from 'react'

import {AgentsCreateSpaceDialog} from './AgentsCreateSpaceDialog'

export const ATTACH_IN_CHAT_BANNER_TEXT =
  'We\'ve updated how you use Spaces. Click "Start chat" next to any of your Spaces, or use the attachments menu in Copilot to select a Space as context for your conversation.'

interface AgentsSpacesListViewProps {
  currentUserLogin: string
  isIndividualAccessEnabled: boolean
}

function CreateSpaceButton() {
  const [open, setOpen] = useState(false)

  return (
    <>
      {open && <AgentsCreateSpaceDialog closeDialog={() => setOpen(false)} spacesBasePath={getAgentsSpacesPath()} />}
      <Button variant="primary" onClick={() => setOpen(true)}>
        Create space
      </Button>
    </>
  )
}

export function AgentsSpacesListView({currentUserLogin, isIndividualAccessEnabled}: AgentsSpacesListViewProps) {
  const navigate = useNavigate()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [attachInChatBannerDismissed, setAttachInChatBannerDismissed] = useLocalStorage(
    'copilot-spaces-attach-in-chat-banner-dismissed',
    false,
  )
  const {categorizedSpaces, categorizedLoading, categorizedFetchingMore, categorizedHasMore, categorizedFetchMore} =
    useFetchPaginatedCategorizedSpaces(true, {individualAccessEnabled: isIndividualAccessEnabled})
  const {data: spacesCounts, isLoading: isCountsLoading} = useFetchSpacesCount(true)
  const {mutateAsync: deleteCopilotSpace} = useDeleteCopilotSpace()
  const [searchQuery, setSearchQuery] = useState('')
  const [deletionError, setDeletionError] = useState<Error | null>(null)
  const {filteredCategorizedSpaces, filteredCategorizedLoading} = useFetchFilteredCategorizedSpaces(
    searchQuery.trim() !== '',
    searchQuery,
    {individualAccessEnabled: isIndividualAccessEnabled},
  )

  const handleDelete = async (space: CopilotSpaceId) => {
    try {
      await deleteCopilotSpace(space)
      setDeletionError(null)
    } catch (error) {
      setDeletionError(error instanceof Error ? error : new Error(String(error)))
      announce('There was a problem deleting your space.')
    }
  }

  return (
    <ErrorBoundary boundaryName="AgentsSpacesListView">
      {deletionError && (
        <Banner
          variant="critical"
          title="Error deleting space"
          hideTitle
          description="There was a problem deleting your space."
        />
      )}
      <ContentView.Container width="small">
        {attachInChatBannerDismissed ? null : (
          <ContentView.Banner>
            <Banner
              title="Attach Spaces in chat"
              hideTitle
              leadingVisual={<PlusIcon />}
              description={ATTACH_IN_CHAT_BANNER_TEXT}
              variant="info"
              onDismiss={() => {
                setAttachInChatBannerDismissed(true)
                // Banner does not restore focus itself, so hand it to the search field
                // rather than dropping the user on the document body.
                searchInputRef.current?.focus()
              }}
            />
          </ContentView.Banner>
        )}
        <ContentView.Header>
          <ContentView.Title>Spaces</ContentView.Title>
          <ContentView.Description>
            Spaces organize your files, pull requests, issues, and standards so Copilot can give more relevant help for
            your work.{' '}
            <Link
              href="https://docs.github.com/copilot/how-tos/provide-context/use-copilot-spaces/create-and-use-copilot-spaces"
              target="_blank"
              inline
              rel="noopener noreferrer"
            >
              Learn more
            </Link>
          </ContentView.Description>
          <ContentView.Actions>
            <CreateSpaceButton />
          </ContentView.Actions>
        </ContentView.Header>

        <SearchInput ref={searchInputRef} onSearch={setSearchQuery} aria-label="Search spaces" />

        <ContentView.Section>
          <SpacesList
            categorizedSpaces={categorizedSpaces}
            categorizedLoading={categorizedLoading}
            filteredCategorizedSpaces={filteredCategorizedSpaces}
            filteredCategorizedLoading={filteredCategorizedLoading}
            spacesCounts={spacesCounts}
            isCountsLoading={isCountsLoading}
            onDelete={handleDelete}
            searchQuery={searchQuery}
            currentUserLogin={currentUserLogin}
            categorizedFetchingMore={categorizedFetchingMore}
            categorizedHasMore={categorizedHasMore}
            categorizedFetchMore={categorizedFetchMore}
            isIndividualAccessEnabled={isIndividualAccessEnabled}
            getSpaceHref={space => getCopilotSpacePath(space, getAgentsSpacesPath())}
            renderSpaceActions={(space, onDelete) => (
              <>
                {!(copilotFeatureFlags.spacesOrgScoping && space.hasOutOfScopePrivateSources) && (
                  <Button
                    size="small"
                    leadingVisual={CommentDiscussionIcon}
                    aria-label={`Start chat with ${space.name} owned by ${space.owner}`}
                    onClick={() => navigate(spaceChatPath(space, getAgentsPath()))}
                  >
                    Start chat
                  </Button>
                )}
                <SpaceMenu
                  space={space}
                  onDelete={onDelete}
                  spacesBasePath={getAgentsSpacesPath()}
                  renderDuplicateDialog={(initialSpace, closeDialog) => (
                    <AgentsCreateSpaceDialog
                      dialogTitle="Duplicate space"
                      closeDialog={closeDialog}
                      initialSpace={initialSpace}
                      spacesBasePath={getAgentsSpacesPath()}
                    />
                  )}
                  showStar
                />
              </>
            )}
            renderCreateSpaceButton={() => <CreateSpaceButton />}
          />
        </ContentView.Section>
      </ContentView.Container>
    </ErrorBoundary>
  )
}
